import React from 'react';
import _ from 'lodash';

import Conditional from '../../../common/conditional';
import PDFPage from '../../../themed/pdf/pdfPage';
import PDFPhotoPage from '../../../themed/pdf/pdfPhotoPage';
import VIAnomalyData from './viAnomalyData';
import VISummarySheet from './viSummarySheet';
import RiskRating from '../riskRating';

import config from '../../../../utils/config';

import { VIReportItem, PDFImageProps, Project } from '../../../../utils/models';
import { getPageDetails } from '../../../../utils/utilities';
import { getReportDetails } from '../../../../store/reportDetails';
import { MAX_ANOMALY_REPORT_PER_PAGE, MAX_IMAGES_PER_ITEM } from '../../../../utils/constants';
import { useAppSelector } from '../../../../store/configureStore';

interface VTPDFDataProps {
	data: VIReportItem[];
	images: PDFImageProps[];
	project: Project;
	remarks: string[];
	summary: string;
	showAnomalyPage: boolean;
}

const VIPDFData: React.FC<VTPDFDataProps> = (props) => {
	const { itemCountPerPage } = useAppSelector(getReportDetails);

	const getSplitData = (limit: number): VIReportItem[][] => {
		return _.chunk(props.data, limit);
	};

	const renderGeneralPhotos = () => {
		const chunkedImages = _.chunk(props.images, 4);
		return chunkedImages.map((images, index) => (
			<PDFPage
				key={index.toString()}
				pageDetails={getPageDetails('General Photos')}
				projectDetails={props.project}
				enableTitlePage={index === 0}
				enableAssociateDetails={false}
			>
				<PDFPhotoPage photos={images} />
			</PDFPage>
		));
	};

	const renderInspectedItemImages = () => {
		return props.data.map((item, index) => {
			const images = _.chunk(item.images.slice(0, MAX_IMAGES_PER_ITEM), 4);

			return images.map((imagePage, imagePageIndex) => {
				const tag: string = item.tagNumber.toUpperCase();

				return (
					<PDFPage
						key={imagePageIndex.toString()}
						pageDetails={getPageDetails(`Photographic Report - ${tag}`, tag)}
						projectDetails={props.project}
						enableTitlePage={index === 0}
						enableAssociateDetails={false}
					>
						<PDFPhotoPage photos={imagePage} />
					</PDFPage>
				);
			});
		});
	};

	return (
		<React.Fragment>
			{getSplitData(itemCountPerPage).map((items, index: number) => {
				const pageTitle: string = 'Summary Sheet';
				const pageSubtitle: string = props.project.documentNumber;

				return (
					<PDFPage
						key={index.toString()}
						pageDetails={getPageDetails(pageTitle, pageSubtitle)}
						projectDetails={props.project}
						enableTitlePage={index === 0}
					>
						<VISummarySheet
							summary={props.summary}
							remarks={props.remarks}
							data={items}
						/>
					</PDFPage>
				);
			})}

			<Conditional isVisible={props.showAnomalyPage}>
				{getSplitData(MAX_ANOMALY_REPORT_PER_PAGE).map((items, index: number) => (
					<PDFPage
						key={index.toString()}
						pageDetails={getPageDetails('Anomaly Data', 'Summary')}
						projectDetails={props.project}
						enableTitlePage={index === 0}
					>
						<VIAnomalyData data={items} />
					</PDFPage>
				))}
			</Conditional>

			{renderInspectedItemImages()}

			{renderGeneralPhotos()}

			<Conditional isVisible={config.enableRiskRating}>
				<PDFPage
					pageDetails={getPageDetails('Risk Rating')}
					projectDetails={props.project}
				>
					<Conditional isVisible={props.data.length > 0}>
						<RiskRating data={props.data} />
					</Conditional>
				</PDFPage>
			</Conditional>
		</React.Fragment>
	);
};

export default VIPDFData;
